import React, { useContext, useState } from "react";
import {
	Box,
	Button,
	Typography,
	FormControl,
	Select,
	MenuItem,
	TextField,
	useMediaQuery,
	CircularProgress,
} from "@mui/material";
import Grid from "@mui/system/Unstable_Grid/Grid";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import AdapterJalaali from "@date-io/jalaali";
import jMoment from "moment-jalaali";
import {
	Chart as ChartJS,
	LinearScale,
	CategoryScale,
	BarElement,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
	LineController,
	BarController,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Chart } from "react-chartjs-2";
import { useTheme } from "@mui/system";
import useForm from "../../shared/hooks/form-hooks";
import { AuthContext } from "../../context/AuthContext";
import { useSnackbar } from "notistack";
import axios from "axios";
import { VALIDATOR_AUTOCOMPLETE } from "../../shared/util/validators";
import Input from "../../shared/components/Input";

const selectPeriods = [
	{ slug: "daily", label: "روزانه" },
	{ slug: "weekly", label: "هفتگی" },
	{ slug: "monthly", label: "ماهانه" },
	{ slug: "yearly", label: "سالانه" },
];

const yearData = [
	{ year: 2010, count: 10 },
	{ year: 2011, count: 20 },
	{ year: 2012, count: 15 },
	{ year: 2013, count: 25 },
	{ year: 2014, count: 22 },
	{ year: 2015, count: 30 },
	{ year: 2016, count: 28 },
	{ year: 2017, count: 28 },
	{ year: 2018, count: 28 },
	{ year: 2019, count: 28 },
];

ChartJS.register(
	LinearScale,
	CategoryScale,
	BarElement,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
	LineController,
	BarController,
	ChartDataLabels
);

const formatData = (period, data) => {
	if (!data) return [];
	//  data is an array of {date: Date().getTime(), count: Number}
	const convertedData = data.map((data) => {
		return { date: parseInt(data.startDate), count: parseInt(data.count) };
	});

	switch (period) {
		case "daily":
			const dailyData = convertedData.reduce((result, item) => {
				const dataKey = item.date;
				if (!result[dataKey]) result[dataKey] = 0;
				result[dataKey] += item.count;
				return result;
			}, {});

			return Object.entries(dailyData)
				.sort(
					(a, b) =>
						new Date(parseInt(a[0])).getTime() -
						new Date(parseInt(b[0])).getTime()
				)
				.reduce(
					(obj, [key, value]) => ({
						...obj,
						[jMoment(parseInt(key)).format("jYYYY/jMM/jDD")]: value,
					}),
					{}
				);
		case "monthly":
			const monthlyData = convertedData.reduce((result, item) => {
				const d = jMoment(item.date);
				const year = d.jYear();
				const month = d.jMonth();
				const dataKey = `${year}-${
					month + 1 < 10 ? `0${month + 1}` : month + 1
				}`;
				if (!result[dataKey]) result[dataKey] = 0;
				result[dataKey] += item.count;
				return result;
			}, {});
			return Object.entries(monthlyData)
				.sort(
					(a, b) =>
						parseInt(a[0].split("-").join("")) -
						parseInt(b[0].split("-").join(""))
				)
				.reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {});
		case "weekly":
			const weeklyData = convertedData.reduce((result, item) => {
				const d = jMoment(item.date);
				const sd = d.day();
				const sdiff = d.date() - sd + (sd === 0 ? -6 : 1);
				const startOfWeek = d.clone().date(sdiff);

				const endOfWeek = startOfWeek.clone().add(6, "days");
				const dataKey = `${new Date(startOfWeek).getTime()}-${new Date(
					endOfWeek
				).getTime()}`;
				if (!result[dataKey]) result[dataKey] = 0;
				result[dataKey] += item.count;
				return result;
			}, {});
			return Object.entries(weeklyData)
				.sort(
					(a, b) =>
						parseInt(a[0].split("-")[0]) -
						parseInt(b[0].split("-")[0])
				)
				.reduce(
					(obj, [key, value]) => ({
						...obj,
						[key
							.split("-")
							.map((d) =>
								jMoment(parseInt(d)).format("jYYYY/jMM/jDD")
							)
							.join(" تا ")]: value,
					}),
					{}
				);
		case "yearly":
			const yearlyData = convertedData.reduce((result, item) => {
				const d = jMoment(item.date);
				const year = d.jYear();
				if (!result[year]) result[year] = 0;
				result[year] += parseInt(item.count);
				return result;
			}, {});
			return Object.entries(yearlyData)
				.sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
				.reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {});
		default:
			return convertedData;
	}
};

const IssuedFactorsChartSection = () => {
	const [rawData, setRawData] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const auth = useContext(AuthContext);
	const { enqueueSnackbar } = useSnackbar();
	const [formState, inputHandler] = useForm({
		period: { value: "monthly", isValid: false },
		startDate: { value: null, isValid: false },
		endDate: { value: null, isValid: false },
	});

	const theme = useTheme();
	const underMd = useMediaQuery(theme.breakpoints.down("md"));
	const gridLineColor = theme.palette.mode === "dark" ? "#3a3a3a" : "#aeaeae";

	const getData = async () => {
		setIsLoading(true);
		await axios
			.post(
				"/api/v1/laboratorys/get-receipt-charts",
				{
					startDate: formState.inputs.startDate.value,
					endDate: formState.inputs.endDate.value,
				},
				{
					headers: {
						"Content-Type": "application/json",
						Authorization: auth.token,
					},
				}
			)
			.then((response) => setRawData(response.data.data))
			.catch((e) => {
				if (e.response.status === 404) {
					setRawData([]);
				} else {
					const messages = e.response.data.data;
					messages.map((message) =>
						enqueueSnackbar(message, { variant: "error" })
					);
				}
			})
			.finally(() => setIsLoading(false));
	};

	return (
		<Box>
			<Grid container spacing={4}>
				<Grid
					xs={12}
					sx={{ display: "flex", alignItems: "flex-start" }}
				>
					<Typography component={"h5"} variant="h5">
						نمودار فاکتورهای صادرشده
					</Typography>
				</Grid>
				<Grid xs={12} pl={0}>
					<form
						onSubmit={(e) => {
							e.preventDefault();
						}}
					>
						<Grid container spacing={4}>
							<Grid xs={12} md={4}>
								<Input
									id="period"
									element="autoComplete"
									label="دوره نمایش"
									validators={[VALIDATOR_AUTOCOMPLETE]}
									errorText="لطفا دوره نمایش را انتخاب کنید."
									onInput={inputHandler}
									options={selectPeriods}
									getOptionLabel={(option) => option.label}
									size="small"
								/>
							</Grid>
							<Grid xs={6} md={4}>
								<FormControl fullWidth>
									<LocalizationProvider
										dateAdapter={AdapterJalaali}
									>
										<DatePicker
											value={
												formState.inputs.startDate.value
											}
											onChange={(date) => {
												inputHandler(
													"startDate",
													new Date(date).getTime(),
													formState.inputs.startDate
														.isValid
												);
											}}
											disableMaskedInput
											renderInput={(params) => (
												<TextField
													{...params}
													fullWidth
													inputProps={{
														placeholder: "از تاریخ",
													}}
													variant={"outlined"}
													size="small"
													value={
														formState.inputs
															.startDate.value
															? jMoment(
																	formState
																		.inputs
																		.startDate
																		.value
															  ).format(
																	"jYYYY/jMM/jDD"
															  )
															: ""
													}
												/>
											)}
										/>
									</LocalizationProvider>
								</FormControl>
							</Grid>
							<Grid xs={6} md={4}>
								<FormControl fullWidth>
									<LocalizationProvider
										dateAdapter={AdapterJalaali}
									>
										<DatePicker
											value={
												formState.inputs.endDate.value
											}
											onChange={(date) => {
												inputHandler(
													"endDate",
													new Date(date).getTime(),
													formState.inputs.endDate
														.isValid
												);
											}}
											disableMaskedInput
											renderInput={(params) => (
												<TextField
													{...params}
													fullWidth
													inputProps={{
														placeholder: "از تاریخ",
													}}
													variant={"outlined"}
													size="small"
													value={
														formState.inputs.endDate
															.value
															? jMoment(
																	formState
																		.inputs
																		.endDate
																		.value
															  ).format(
																	"jYYYY/jMM/jDD"
															  )
															: ""
													}
													minDate={
														formState.inputs
															.startDate.value
													}
												/>
											)}
										/>
									</LocalizationProvider>
								</FormControl>
							</Grid>
							<Grid xs={12}>
								<Button
									fullWidth
									variant="outlined"
									color="secondary"
									type="submit"
									disabled={
										isLoading ||
										!formState.inputs.startDate.value ||
										!formState.inputs.endDate.value
									}
									onClick={getData}
								>
									نمایش نمودار
								</Button>
							</Grid>
						</Grid>
					</form>
				</Grid>

				{isLoading ? (
					<Grid xs={12} padding={0} justifyContent={"center"}>
						<CircularProgress />
					</Grid>
				) : rawData === null ? (
					<></>
				) : (
					<Grid
						xs={12}
						padding={0}
						height={underMd ? "250px" : "320px"}
					>
						<GridContent
							data={rawData}
							theme={theme}
							underMd={underMd}
							gridLineColor={gridLineColor}
							period={formState.inputs.period.value.slug}
						/>
					</Grid>
				)}
			</Grid>
		</Box>
	);
};

const GridContent = ({ data, theme, underMd, gridLineColor, period }) => {
	const formattedData = formatData(period, data);
	const max = Math.max(
		Object.values(formattedData).map((n) => parseInt(n)),
		10
	);
	if (data === null) {
		return <></>;
	} else if (data.length === 0) {
		return (
			<Typography variant="h6">داده‌ای برای نمایش یافت نشد.</Typography>
		);
	} else {
		return (
			<>
				{/*<Grid xs={12} padding={0} height={underMd ? "250px" : "320px"}>
					 <Chart
						type="bar"
						data={{
							labels: Object.keys(formattedData),
							datasets: [
								{
									type: "bar",
									order: 1,
									label: "تعداد",
									data: Object.values(formattedData),
									backgroundColor:
										theme.palette.secondary.main,
									barPercentage: underMd ? "0.95" : "0.4",
									datalabels: {
										anchor: "middle",
										align: "center",
									},
								},
							],
						}}
						options={{
							plugins: {
								datalabels: {
									backgroundColor: function (context) {
										return underMd
											? "transparent"
											: context.dataset.backgroundColor;
									},
									borderRadius: 4,
									color:
										theme.palette.mode === "dark"
											? "black"
											: "white",
									font: {
										size: underMd ? "10px" : "13px",
										weight: underMd ? "normal" : "bold",
									},
									textAlign: "center",
									formatter: Math.round,
									padding: underMd ? 1 : 4,
								},
							},
							font: { family: "Yekan" },
							layout: { padding: 10 },
							maintainAspectRatio: false,
							responsive: true,
							scales: {
								x: {
									stacked: true,
									grid: {
										color: gridLineColor,
										tickColor: gridLineColor,
									},
								},
								y: {
									min: 0,
									max: max,
									stacked: true,
									grid: {
										color: gridLineColor,
										tickColor: gridLineColor,
									},
								},
							},
						}}
					/>
				</Grid> */}
				<Grid xs={12} padding={0} height={underMd ? "250px" : "320px"}>
					<Chart
						type="bar"
						data={{
							labels: Object.keys(formattedData),
							datasets: [
								{
									type: "line",
									order: 0,
									label: "تعداد",
									data: Object.values(formattedData),
									backgroundColor: "blue",
									barPercentage: "0.4",
									borderColor: "blue",
									datalabels: {
										anchor: "start",
										align: "center",
									},
								},
							],
						}}
						options={{
							plugins: {
								datalabels: {
									backgroundColor: function (context) {
										return context.dataset.backgroundColor;
									},
									borderRadius: 4,
									color:
										theme.palette.mode === "dark"
											? "black"
											: "white",
									font: {
										size: underMd ? "10px" : "13px",
										weight: underMd ? "normal" : "bold",
									},
									textAlign: "center",
									formatter: Math.round,
									padding: underMd ? 2 : 4,
								},
							},
							font: { family: "Yekan" },
							layout: { padding: 10 },
							maintainAspectRatio: false,
							responsive: true,
							scales: {
								x: {
									stacked: true,
									grid: {
										color: gridLineColor,
										tickColor: gridLineColor,
									},
								},
								y: {
									min: 0,
									max: max,
									stacked: true,
									grid: {
										color: gridLineColor,
										tickColor: gridLineColor,
									},
								},
							},
						}}
					/>
				</Grid>
			</>
		);
	}
};

export default IssuedFactorsChartSection;
